// src/pages/DayEditor.js — 하루 식단 편집 (캘린더 오른쪽 패널 안)
//   끼니 섹션 → 음식 줄(섭취량 바로 수정) → 하단 합계 + 저장. 저장은 하루 단위 교체(지운 음식·끼니는 서버에서도 삭제)
import React, { useEffect, useMemo, useState } from 'react';
import { getCodeSearch, getDietHistDayInfo, postHistDaySave } from '../api/dietApi';
import FoodPicker from '../components/ui/FoodPicker';
import EmptyState from '../components/ui/EmptyState';
import { MacroStrip, MacroText, fmt } from '../components/ui/Nutrients';
import { useToast } from '../components/ui/Toast';

const DEFAULT_CODES = [
  { code: 'BR', codeNm: '아침' }, { code: 'LC', codeNm: '점심' },
  { code: 'DN', codeNm: '저녁' }, { code: 'SN', codeNm: '간식' },
];
const MAX_MEALS = 5;
const MEAL_ORDER = ['BR', 'LC', 'DN', 'SN'];
let seq = 0;
const uid = () => `m${++seq}`;

// 서버 하루 기록 → 끼니 목록 (dietHistSeq + dietCd 그룹)
const toMeals = (list) => {
  const map = new Map();
  list.forEach((it) => {
    const key = `${it.dietHistSeq}_${it.dietCd}`;
    if (!map.has(key)) map.set(key, { id: uid(), type: it.dietCd, dietHistSeq: it.dietHistSeq, foods: [] });
    // 하루 기록 API 는 섭취량 반영값(*Calc)만 준다 → 100g 기준으로 되돌려 둬야 섭취량을 고쳐도 다시 계산된다
    const g = Number(it.intake) || 0;
    const per100 = (v) => (g > 0 ? ((Number(v) || 0) * 100) / g : 0);
    map.get(key).foods.push({
      id: uid(),
      foodCd: it.foodCd,
      foodNm: it.foodNm,
      intake: String(g),
      per: { kcal: per100(it.kcalCalc), carbo: per100(it.carboCalc), protein: per100(it.proteinCalc), fat: per100(it.fatCalc) },
    });
  });
  return [...map.values()].sort((x, y) => MEAL_ORDER.indexOf(x.type) - MEAL_ORDER.indexOf(y.type));
};

const calc = (food) => {
  const g = Number(food.intake) || 0;
  return {
    kcal: (food.per.kcal * g) / 100,
    carbo: (food.per.carbo * g) / 100,
    protein: (food.per.protein * g) / 100,
    fat: (food.per.fat * g) / 100,
  };
};

const total = (foods) => foods.map(calc).reduce(
  (s, n) => ({ kcal: s.kcal + n.kcal, carbo: s.carbo + n.carbo, protein: s.protein + n.protein, fat: s.fat + n.fat }),
  { kcal: 0, carbo: 0, protein: 0, fat: 0 },
);

const snapshot = (meals) => JSON.stringify(meals.map((m) => [m.type, m.dietHistSeq, m.foods.map((f) => [f.foodCd, f.intake])]));

export default function DayEditor({ date, onDirtyChange, onSaved }) {
  const toast = useToast();
  const [codes, setCodes] = useState(DEFAULT_CODES);
  const [meals, setMeals] = useState([]);
  const [saved, setSaved] = useState('[]');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickFor, setPickFor] = useState(null);   // 음식 추가할 끼니 id

  useEffect(() => {
    Promise.all([
      getCodeSearch('dietCd').then((r) => r.data).catch(() => null),
      getDietHistDayInfo(date).then((r) => r.data).catch(() => []),
    ]).then(([c, day]) => {
      if (Array.isArray(c) && c.length) setCodes(c);
      const ms = toMeals(Array.isArray(day) ? day : []);
      setMeals(ms);
      setSaved(snapshot(ms));
      setLoading(false);
    });
  }, [date]);

  const dirty = !loading && snapshot(meals) !== saved;
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const codeNm = (c) => codes.find((x) => x.code === c)?.codeNm ?? c;
  const dayTotal = useMemo(() => total(meals.flatMap((m) => m.foods)), [meals]);

  const updateMeal = (id, fn) => setMeals((prev) => prev.map((m) => (m.id === id ? fn(m) : m)));

  const addMeal = () => {
    if (meals.length >= MAX_MEALS) { toast(`끼니는 최대 ${MAX_MEALS}개까지 추가할 수 있습니다.`, { type: 'error' }); return; }
    const used = meals.map((m) => m.type);
    const next = ['BR', 'LC', 'DN'].find((c) => !used.includes(c)) ?? 'SN';
    const meal = { id: uid(), type: next, dietHistSeq: null, foods: [] };
    setMeals((prev) => [...prev, meal]);
    setPickFor(meal.id);
  };

  const addFood = (food, intake) => {
    updateMeal(pickFor, (m) => ({
      ...m,
      foods: [...m.foods, {
        id: uid(), foodCd: food.foodCd, foodNm: food.foodNm, intake: String(intake),
        per: { kcal: Number(food.kcal) || 0, carbo: Number(food.carbo) || 0, protein: Number(food.protein) || 0, fat: Number(food.fat) || 0 },
      }],
    }));
    setPickFor(null);
    toast(`${food.foodNm} ${intake}g 추가`);
  };

  const save = async () => {
    const bad = meals.some((m) => m.foods.some((f) => !(Number(f.intake) >= 1)));
    if (bad) { toast('섭취량은 1g 이상 입력해 주세요.', { type: 'error' }); return; }
    setSaving(true);
    try {
      const payload = meals.map((m) => ({
        dietCd: m.type,
        dietHistSeq: m.dietHistSeq ?? null,
        dietHistDtlDtoList: m.foods.map((f) => ({ foodCd: f.foodCd, intake: Number(f.intake) })),
      }));
      const res = await postHistDaySave(date, payload);
      // 새로 생긴 끼니 번호를 받아 두어야 다시 저장해도 중복되지 않는다
      const ms = toMeals(Array.isArray(res.data) ? res.data : []);
      setMeals(ms);
      setSaved(snapshot(ms));
      onSaved?.();
      toast('저장했습니다.', { type: 'success' });
    } catch (e) {
      toast(e?.response?.data?.message || '저장 중 오류가 발생했습니다.', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="day-msg">불러오는 중…</p>;

  return (
    <div className="day">
      {meals.length === 0 ? (
        <EmptyState message="이 날은 아직 기록이 없어요." actionLabel="첫 끼니 기록하기" onAction={addMeal} />
      ) : meals.map((m) => {
        const t = total(m.foods);
        return (
          <section key={m.id} className="day-meal">
            <header className="day-meal-head">
              <select className="select" value={m.type} onChange={(e) => updateMeal(m.id, (x) => ({ ...x, type: e.target.value }))}>
                {codes.map((c) => <option key={c.code} value={c.code}>{c.codeNm}</option>)}
              </select>
              <span className="day-meal-kcal">{fmt(t.kcal)} kcal</span>
              <button className="btn ghost danger" type="button" onClick={() => setMeals((prev) => prev.filter((x) => x.id !== m.id))}>끼니 삭제</button>
            </header>

            {m.foods.length > 0 && <MacroStrip carbo={t.carbo} protein={t.protein} fat={t.fat} />}

            <ul className="day-foods">
              {m.foods.map((f) => {
                const n = calc(f);
                return (
                  <li key={f.id} className="day-food">
                    <div className="day-food-main">
                      <strong>{f.foodNm}</strong>
                      <MacroText carbo={n.carbo} protein={n.protein} fat={n.fat} />
                    </div>
                    <label className="day-food-g">
                      <input
                        className="input" type="number" min="1" value={f.intake}
                        aria-label={`${f.foodNm} 섭취량`}
                        onChange={(e) => updateMeal(m.id, (x) => ({ ...x, foods: x.foods.map((y) => (y.id === f.id ? { ...y, intake: e.target.value } : y)) }))}
                      />
                      g
                    </label>
                    <span className="day-food-kcal">{fmt(n.kcal)}</span>
                    <button className="icon-btn" type="button" aria-label={`${f.foodNm} 삭제`}
                      onClick={() => updateMeal(m.id, (x) => ({ ...x, foods: x.foods.filter((y) => y.id !== f.id) }))}>✕</button>
                  </li>
                );
              })}
            </ul>
            <button className="btn ghost day-add-food" type="button" onClick={() => setPickFor(m.id)}>+ {codeNm(m.type)} 음식 추가</button>
          </section>
        );
      })}

      {meals.length > 0 && meals.length < MAX_MEALS && (
        <button className="btn day-add-meal" type="button" onClick={addMeal}>+ 끼니 추가</button>
      )}

      <div className="day-foot">
        <div className="day-total">
          <strong>합계 {fmt(dayTotal.kcal)} kcal</strong>
          <MacroText carbo={dayTotal.carbo} protein={dayTotal.protein} fat={dayTotal.fat} />
        </div>
        <button className="btn primary" type="button" disabled={!dirty || saving} onClick={save}>
          {saving ? '저장 중…' : dirty ? '저장' : '저장됨'}
        </button>
      </div>

      <FoodPicker
        open={!!pickFor}
        title={`${codeNm(meals.find((m) => m.id === pickFor)?.type)} 음식 추가`}
        onClose={() => {
          // 새 끼니를 만들다 취소했으면 빈 끼니는 지운다
          setMeals((prev) => prev.filter((m) => m.id !== pickFor || m.foods.length > 0 || m.dietHistSeq));
          setPickFor(null);
        }}
        onPick={addFood}
      />
    </div>
  );
}
